import React from 'react'
import { Box, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import type { ReconBridgeLine, ReconMeasures, ReconPost, ReconStatus } from '../../../hooks/socialdash/useSkyReconciliation'
import { diffLabel, fmtInt, fmtReach, monthLabel, platformLabel, reasonLabel, safeHref, stepEffect, unexplainedColor } from '../reconciliationModel'

/**
 * The posts ledger (design/2026-10-19-sky-reconciliation-page-redesign.md, M1
 * and M2): Sprinklr beside Platinum on Sky's terms for each measure, then why
 * they differ as a plain list, one line per reason with how many posts it
 * covers. Posts that need a look are listed right there; colour only for them,
 * and red only beyond the tolerance.
 */
export interface PostsLedgerProps {
  sprinklr: ReconMeasures
  /** Platinum on Sky's terms (the bridge's `matched`). */
  platinum: ReconMeasures
  steps: ReconBridgeLine[]
  /** Platinum as the dashboards count, where the sum below starts (the bridge's `platinum`). */
  dashboards?: ReconMeasures
  /** The bridge's "being checked" line: what is left between the two figures. */
  notExplained?: ReconBridgeLine
  needsALook: ReconPost[]
  status: ReconStatus
}

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
const SHOW_AT_MOST = 20

const PostsLedger: React.FC<PostsLedgerProps> = ({ sprinklr, platinum, steps, dashboards, notExplained, needsALook, status }) => {
  const rows: Array<[string, number, number, (n: number) => string]> = [
    ['Posts', sprinklr.posts, platinum.posts, fmtInt],
    ['Reach', sprinklr.reach, platinum.reach, fmtReach],
    ['Views', sprinklr.views, platinum.views, fmtReach],
    ['Engagements', sprinklr.engagements, platinum.engagements, fmtReach],
  ]
  const flag = unexplainedColor(status)
  const lookKeys = new Set(needsALook.map((p) => p.key))
  const nLook = lookKeys.size
  const nPosts = (n: number) => `${fmtInt(n)} post${n === 1 ? '' : 's'}`
  const intro = dashboards
    ? "From Platinum's dashboards to Sprinklr's count, one line per reason."
    : 'One line per reason, with what it does to the numbers.'
  const note = nLook > 0
    ? `Platinum (on Sky's terms) in the table above is this sum less the ${nPosts(nLook)} being checked.`
    : "Platinum (on Sky's terms) in the table above is this sum."
  const sumLine = (testId: string, text: string) => (
    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, ...num }} data-testid={testId}>{text}</Typography>
  )
  const effectLine = (text: string) => (
    <Typography variant="caption" sx={{ display: 'block', ...num, whiteSpace: 'normal' }}>{text}</Typography>
  )
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="recon-posts">
      <Typography variant="h6">Posts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each post by its published UTC day. Platinum here is on Sky&apos;s terms: the dashboards&apos; numbers with the
        agreed adjustments below.
      </Typography>
      <Table size="small" data-testid="recon-posts-ledger">
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>Measure</TableCell>
            <TableCell align="right">Sprinklr</TableCell>
            <TableCell align="right">Platinum</TableCell>
            <TableCell align="right" sx={{ pr: 0 }}>%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, s, p, fmt]) => (
            <TableRow key={label}>
              <TableCell component="th" scope="row" sx={{ pl: 0 }}>{label}</TableCell>
              <TableCell align="right" sx={num}>{fmt(s)}</TableCell>
              <TableCell align="right" sx={num}>{fmt(p)}</TableCell>
              <TableCell align="right" sx={{ ...num, pr: 0 }}>{diffLabel(p, s)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" sx={{ mt: 2.5, fontWeight: 600 }}>How the numbers line up</Typography>
      {(steps.length > 0 || nLook > 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }} data-testid="recon-posts-why-intro">{intro}</Typography>
      )}
      {dashboards && (steps.length > 0 || nLook > 0) && sumLine('recon-posts-sum-start', `Platinum's dashboards: ${nPosts(dashboards.posts)}`)}
      <Box component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="recon-posts-why">
        {steps.map((s) => (
          <Typography component="li" variant="body2" key={s.reason} sx={{ mt: 0.75 }}>
            <Box component="span" sx={num}>{fmtInt(s.count)}</Box> post{s.count === 1 ? '' : 's'}: {reasonLabel(s.reason)}
            {effectLine(stepEffect(s.amount))}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.explanation}</Typography>
          </Typography>
        ))}
        {nLook > 0 && (
          <Typography component="li" variant="body2" sx={{ mt: 0.75 }} color={`${flag}.main`}>
            <Box component="span" sx={num}>{fmtInt(nLook)}</Box> post{nLook === 1 ? '' : 's'}: {reasonLabel('unexplained')}
            {notExplained && effectLine(stepEffect(notExplained.amount))}
          </Typography>
        )}
        {steps.length === 0 && nLook === 0 && (
          <Typography component="li" variant="body2">Nothing: every post matches.</Typography>
        )}
      </Box>
      {dashboards && (steps.length > 0 || nLook > 0) && (
        <>
          {sumLine('recon-posts-sum-end', `= Sprinklr's count: ${nPosts(sprinklr.posts)}`)}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="recon-posts-sum-note">{note}</Typography>
        </>
      )}

      {needsALook.length > 0 && (
        <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: 3, borderColor: `${flag}.main` }} data-testid="recon-needs-a-look">
          {needsALook.slice(0, SHOW_AT_MOST).map((p) => {
            const href = safeHref(p.url)
            return (
              <Box key={`${p.key}-${p.explanation}`} sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {platformLabel(p.platform)} · {monthLabel(p.month)}
                  {href && (
                    <>
                      {' · '}
                      <Link href={href} target="_blank" rel="noopener noreferrer" color="info.main">Open the post</Link>
                    </>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">{p.explanation}</Typography>
              </Box>
            )
          })}
          {needsALook.length > SHOW_AT_MOST && (
            <Typography variant="caption" color="text.secondary">
              And {fmtInt(needsALook.length - SHOW_AT_MOST)} more, in the working below.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  )
}

export default PostsLedger
